import {useEffect, useRef, useState} from 'react'
import {View} from 'react-native'
// @ts-expect-error missing types
import QRCode from 'react-native-qrcode-styled'
import {Trans, useLingui} from '@lingui/react/macro'

import {logger} from '#/logger'
import {useSessionApi} from '#/state/session'
import {FormContainer} from '#/screens/Login/FormContainer'
import {atoms as a, useTheme} from '#/alf'
import {Button, ButtonText} from '#/components/Button'
import {Loader} from '#/components/Loader'
import {Text} from '#/components/Typography'
import {IS_WEB_MOBILE_ANDROID, IS_WEB_MOBILE_IOS} from '#/env'

/*
 * The clearlife:// deep link only works on a phone that can have the Life
 * app installed - gate on device (user agent), not viewport width.
 */
const IS_MOBILE_DEVICE = IS_WEB_MOBILE_IOS || IS_WEB_MOBILE_ANDROID
import {type QrLoginTokens} from './api'
import {useQrLoginPollQuery, useQrLoginSessionQuery} from './useQrLoginSession'

/**
 * "Sign in with Life app" panel: shows a QR code (or a deep link button on
 * mobile web) and waits for the Life app to approve the session, then logs
 * the user in with the tokens minted by the PDS. Auto-creates the account
 * server-side on first login.
 */
export function QrLoginPanel({
  serviceUrl,
  onAttemptSuccess,
  onPressPasswordLogin,
}: {
  serviceUrl: string
  onAttemptSuccess?: () => void
  onPressPasswordLogin?: () => void
}) {
  const {t: l} = useLingui()
  const t = useTheme()
  const {resumeSession} = useSessionApi()
  const [signingIn, setSigningIn] = useState(false)
  const [error, setError] = useState('')
  const consumedRef = useRef(false)

  const session = useQrLoginSessionQuery({serviceUrl})
  const poll = useQrLoginPollQuery({
    serviceUrl,
    verkey: session.data?.verkey,
    enabled: !session.expired && !signingIn,
  })

  const tokens = poll.data?.status === 'ready' ? poll.data.tokens : undefined
  useEffect(() => {
    if (!tokens || consumedRef.current) return
    consumedRef.current = true
    setSigningIn(true)
    void signInWithTokens(tokens)
    async function signInWithTokens(tokens: QrLoginTokens) {
      try {
        await resumeSession({
          service: serviceUrl,
          did: tokens.did,
          handle: tokens.handle,
          accessJwt: tokens.accessJwt,
          refreshJwt: tokens.refreshJwt,
          emailConfirmed: false,
          active: tokens.active,
        })
        onAttemptSuccess?.()
      } catch (err) {
        logger.error('QR login: failed to resume session', {
          safeMessage: err,
        })
        setError(
          l`Something went wrong signing you in. Please refresh the QR code and try again.`,
        )
        setSigningIn(false)
      }
    }
  }, [tokens, resumeSession, serviceUrl, onAttemptSuccess, l])

  const onRefresh = () => {
    consumedRef.current = false
    setError('')
    setSigningIn(false)
    session.refresh()
  }

  const openLifeApp = () => {
    if (session.data) {
      window.location.href = session.data.deepLink
    }
  }

  let content
  if (error || session.isError || poll.isError) {
    content = (
      <View style={[a.gap_md, a.align_center, a.py_xl]}>
        <Text style={[a.text_md, a.text_center, t.atoms.text_contrast_high]}>
          {error || (
            <Trans>
              Unable to reach the server. Please check your Internet connection.
            </Trans>
          )}
        </Text>
        <Button
          testID="qrLoginRetryButton"
          label={l`Try again`}
          onPress={onRefresh}
          color="primary"
          size="large">
          <ButtonText>
            <Trans>Try again</Trans>
          </ButtonText>
        </Button>
      </View>
    )
  } else if (signingIn) {
    content = (
      <View style={[a.gap_md, a.align_center, a.py_xl]}>
        <Loader size="xl" />
        <Text style={[a.text_md, t.atoms.text_contrast_medium]}>
          <Trans>Signing you in…</Trans>
        </Text>
      </View>
    )
  } else if (session.expired) {
    content = (
      <View style={[a.gap_md, a.align_center, a.py_xl]}>
        <Text style={[a.text_md, t.atoms.text_contrast_medium]}>
          <Trans>This QR code has expired.</Trans>
        </Text>
        <Button
          testID="qrLoginRefreshButton"
          label={l`Refresh QR code`}
          onPress={onRefresh}
          color="primary"
          size="large">
          <ButtonText>
            <Trans>Refresh QR code</Trans>
          </ButtonText>
        </Button>
      </View>
    )
  } else if (!session.data) {
    content = (
      <View style={[a.align_center, a.py_xl]}>
        <Loader size="xl" />
      </View>
    )
  } else {
    content = (
      <View style={[a.gap_lg, a.align_center]}>
        {IS_MOBILE_DEVICE && (
          <Button
            testID="qrLoginOpenAppButton"
            label={l`Open in Life app`}
            onPress={openLifeApp}
            color="primary"
            size="large"
            style={[a.self_stretch]}>
            <ButtonText>
              <Trans>Open in Life app</Trans>
            </ButtonText>
          </Button>
        )}
        <Text style={[a.text_md, a.text_center, t.atoms.text_contrast_medium]}>
          {IS_MOBILE_DEVICE ? (
            <Trans>
              Or scan this QR code with the Life app on another device:
            </Trans>
          ) : (
            <Trans>
              Scan this QR code with the Life app on your phone to sign in. If
              you don’t have an account yet, one will be created for you.
            </Trans>
          )}
        </Text>
        <View style={[a.rounded_md, a.overflow_hidden]}>
          <QRCode
            testID="qrLoginCode"
            data={session.data.qrContent}
            style={{backgroundColor: '#ffffff'}}
            pieceSize={5}
            padding={16}
            color="#000000"
          />
        </View>
        <View style={[a.flex_row, a.align_center, a.gap_sm]}>
          <Loader size="sm" />
          <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
            <Trans>Waiting for approval…</Trans>
          </Text>
        </View>
      </View>
    )
  }

  return (
    <FormContainer testID="qrLoginForm" titleText={<Trans>Sign in</Trans>}>
      {content}
      {onPressPasswordLogin && (
        <Button
          testID="qrLoginUsePasswordButton"
          label={l`Sign in with password instead`}
          onPress={onPressPasswordLogin}
          color="secondary"
          size="large">
          <ButtonText>
            <Trans>Sign in with password instead</Trans>
          </ButtonText>
        </Button>
      )}
    </FormContainer>
  )
}
